import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { Text, Capitalize, Icon, ContentEditable } from '../../atoms';

const StyledNavLink = styled(NavLink)`
  text-decoration: none;
  width: 100%;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  visibility: hidden;
`;

const StyledNavigationItem = styled(Text)`
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? 'var(--content-body)' : 'var(--content-subtitle)')};
  background-color: ${({ $active }) => ($active ? 'var(--wash-gray-default)' : 'transparent')};
  transition: background-color 0.2s ease-in-out;
  border-radius: 6px;
  &:hover {
    color: var(--content-body);
    ${ActionsContainer} {
      visibility: visible;
    }
  }
`;

const StyledText = styled(Text)`
  font-weight: 500;
  font-family: var(--font-family-title);
  margin-left: ${({ $isShared }) => ($isShared ? '0' : '24px')};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  transition: color 0.2s ease-in-out;
`;

const StyledContentEditable = styled(ContentEditable)`
  width: 100%;
  font-weight: 500;
  font-family: var(--font-family-title);
  margin-left: ${({ $isShared }) => ($isShared ? '0' : '24px')};
  padding: 5px;
  min-width: 0;
  max-width: 100%;
  border-radius: 6px;
  overflow: visible;
  white-space: normal;
  word-break: break-word;
  border: 1px solid var(--primary-default);
`;

const NavigationItem = ({
  url,
  label,
  className = null,
  onClick = null,
  isShared = false,
  onCompleteEditing = null,
  actions = null
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      setTimeout(() => {
        if (titleRef.current) {
          titleRef.current.focus();
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(titleRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 0);
    }
  }, [isEditing]);

  const handleBlur = useCallback(
    (evt) => {
      setIsEditing(false);
      if (evt.target.innerText.trim() === '') titleRef.current.innerText = label;
      titleRef.current.innerText = evt.target.innerText.trim();
      onCompleteEditing?.(titleRef.current.innerText);
    },
    [onCompleteEditing, label]
  );

  const handleKeyDown = (evt) => {
    if (evt.key === 'Enter') evt.target.blur();
  };

  return (
    <StyledNavLink to={url} onClick={onClick}>
      {({ isActive }) => (
        <StyledNavigationItem $active={isActive} className={className}>
          {isShared && <Icon name='shared' size={16} />}
          {isEditing ? (
            <StyledContentEditable
              ref={titleRef}
              contentEditable
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
            >
              {label}
            </StyledContentEditable>
          ) : (
            <StyledText as='span' $color='inherit' $isShared={isShared}>
              <Capitalize string={label} />
            </StyledText>
          )}
          {actions && <ActionsContainer>{actions(setIsEditing)}</ActionsContainer>}
        </StyledNavigationItem>
      )}
    </StyledNavLink>
  );
};
NavigationItem.propTypes = {
  url: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  isShared: PropTypes.bool,
  onCompleteEditing: PropTypes.func,
  actions: PropTypes.func
};
export { NavigationItem };
